import { useMemo, useState } from 'react';
import { OFF } from '../../lib/off';
import { Link } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type E911Address, type NumberE911, type SavedAddress } from '../../api/client';
import { AddressChooser, AddressFields, EMPTY_ADDRESS, addressErrors, choiceOf, draftAddress, draftErrors, draftFor, oneLine, type AddressDraft } from '../../components/numbers';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { plural, shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

type Show = 'all' | 'missing';

/**
 * Emergency addresses (E911): where emergency services are sent when someone calls 911 from each of
 * the company's numbers, the addresses the company keeps, and the name shown on calls out.
 */
export function TenantEmergency() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const view = useData(() => api.e911(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const [show, setShow] = useState<Show>('all');
  const [editing, setEditing] = useState<NumberE911 | null>(null);
  const [naming, setNaming] = useState<NumberE911 | null>(null);
  const [addr, setAddr] = useState<SavedAddress | 'new' | null>(null);
  const [toast, setToast] = useState('');

  const numbers = view.data?.numbers ?? [];
  const saved = view.data?.addresses ?? [];
  const canChange = Boolean(view.data?.carrier && view.data.writes && tenant.writable);
  const needs = (n: NumberE911) => n.atCarrier && !n.tollFree && !n.address;
  const missing = numbers.filter(needs).length;
  const rows = useMemo(() => (show === 'missing' ? numbers.filter(needs) : numbers), [numbers, show]);
  // Which saved address a number uses, by name.
  const labelOf = (a: E911Address | null) => (a ? saved.find((s) => s.address1.toLowerCase() === a.address1.toLowerCase() && s.zip.slice(0, 5) === a.zip.slice(0, 5) && (s.address2 ?? '').toLowerCase() === (a.address2 ?? '').toLowerCase())?.label : undefined);

  return (
    <>
      <PageHeader eyebrow={view.data ? `${plural(numbers.length, 'number')}${missing ? ` · ${missing} without an address` : ''}` : d} title="Emergency addresses" />
      <div className="content">
        <Notice icon="pin" title="Where 911 sends help">
          When someone calls 911, emergency services see the address of the number they call from. Keep each number’s address where the people using it are, including the floor or suite, and update it when they move.
        </Notice>
        {view.data && !view.data.carrier && <Notice icon="lock">Emergency addresses are set by Stitch support for now. Send us a message from the Help button to change one.</Notice>}
        {view.data?.carrier && !view.data.writes && <Notice icon="lock">{OFF.emergencySpendingOff}</Notice>}
        {view.data?.carrier && view.data.writes && !tenant.writable && <Notice icon="lock">{d} is read-only in this environment.</Notice>}
        {missing > 0 && (
          <div className="filters" role="group" aria-label="Show">
            <button type="button" aria-pressed={show === 'all'} onClick={() => setShow('all')}>
              All <span className="filter-count">{numbers.length}</span>
            </button>
            <button type="button" aria-pressed={show === 'missing'} onClick={() => setShow('missing')}>
              No address <span className="filter-count">{missing}</span>
            </button>
          </div>
        )}
        <Card>
          {view.error ? (
            <LoadError error={view.error} retry={view.reload} />
          ) : !view.data ? (
            <Loading label="Loading emergency addresses" />
          ) : numbers.length === 0 ? (
            <Empty title="No numbers yet">
              Once your company has numbers, set their emergency addresses here. <Link to={`${base}/numbers`}>Numbers</Link>
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">911 address</th>
                    <th scope="col">Name on calls out</th>
                    {canChange && (
                      <th scope="col">
                        <span className="sr-only">Change</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((n) => {
                    const pretty = formatPhoneNumber(n.number);
                    const label = labelOf(n.address);
                    return (
                      <tr key={n.number}>
                        <td>
                          <span className="cell-stack">
                            <span className="mono">{pretty}</span>
                            <small className="muted">{n.destination ? lookup(dir.data, n.destination).name : 'Not going anywhere yet'}</small>
                          </span>
                        </td>
                        <td>
                          {!n.atCarrier ? (
                            <span className="cell-stack">
                              <span className="muted">Set by Stitch support</span>
                              <small className="muted">This number is with another carrier.</small>
                            </span>
                          ) : n.tollFree ? (
                            <span className="muted">Not needed for toll-free</span>
                          ) : !n.inService ? (
                            <Chip small tone="brand">
                              Moving in
                            </Chip>
                          ) : n.address ? (
                            <span className="cell-stack">
                              <span>{label ?? n.address.address1}</span>
                              <small className="muted">{label ? oneLine(n.address) : oneLine({ ...n.address, address1: '' })}</small>
                            </span>
                          ) : (
                            <Chip small tone="warn">
                              <Icon name="alert" size={13} strokeWidth={2.2} /> No address
                            </Chip>
                          )}
                        </td>
                        <td>
                          {n.callerName ? (
                            <span className="cell-stack">
                              <span>{n.callerName.name}</span>
                              <small className="muted">
                                {n.callerName.by}, {shortWhen(n.callerName.at)}
                              </small>
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        {canChange && (
                          <td className="cell-action">
                            {n.atCarrier && (
                              <span className="row-actions">
                                {!n.tollFree && n.inService && (
                                  <Button size="small" variant={n.address ? 'secondary' : 'primary'} icon="pin" onClick={() => setEditing(n)} aria-label={`${n.address ? 'Change' : 'Set'} the 911 address of ${pretty}`}>
                                    {n.address ? 'Change' : 'Set address'}
                                  </Button>
                                )}
                                <Button size="small" variant="ghost" onClick={() => setNaming(n)} aria-label={`Name shown on calls out from ${pretty}`}>
                                  Name
                                </Button>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Saved addresses"
          action={
            tenant.writable && view.data?.carrier ? (
              <Button size="small" icon="plus" onClick={() => setAddr('new')}>
                Add an address
              </Button>
            ) : undefined
          }
        >
          {!view.data ? null : saved.length === 0 ? (
            <Empty title="No saved addresses">Save your offices here to choose them quickly for new numbers.</Empty>
          ) : (
            <ul className="address-list">
              {saved.map((a) => (
                <li key={a.id}>
                  <Icon name="pin" size={17} />
                  <span className="cell-stack grow">
                    <strong>{a.label}</strong>
                    <small className="muted">{oneLine(a)}</small>
                  </span>
                  {tenant.writable && (
                    <Button size="small" variant="ghost" icon="edit" onClick={() => setAddr(a)} aria-label={`Edit ${a.label}`}>
                      Edit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {editing && (
        <SetAddress
          d={d}
          number={editing}
          saved={saved}
          onClose={() => setEditing(null)}
          onDone={(message) => {
            setEditing(null);
            setToast(message);
            view.reload();
          }}
        />
      )}
      {naming && (
        <CallerName
          d={d}
          number={naming}
          onClose={() => setNaming(null)}
          onDone={(message) => {
            setNaming(null);
            setToast(message);
            view.reload();
          }}
        />
      )}
      {addr && (
        <EditAddress
          d={d}
          address={addr === 'new' ? null : addr}
          onClose={() => setAddr(null)}
          onDone={(message) => {
            setAddr(null);
            setToast(message);
            view.reload();
          }}
        />
      )}
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </>
  );
}

/** Setting (or changing) a number's 911 address, with a confirmation; or removing it. */
function SetAddress({ d, number, saved, onClose, onDone }: { d: string; number: NumberE911; saved: SavedAddress[]; onClose: () => void; onDone: (message: string) => void }) {
  const pretty = formatPhoneNumber(number.number);
  const [draft, setDraft] = useState<AddressDraft>(() => draftFor(saved));
  const [confirm, setConfirm] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState(false);
  const chosen = draftAddress(draft, saved);

  async function save() {
    setTried(true);
    if (Object.keys(draftErrors(draft)).length || !confirm) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.setE911(d, number.number, choiceOf(draft)!);
      onDone(`${pretty}’s 911 address is ${oneLine(r.address)}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError('');
    try {
      await api.removeE911(d, number.number);
      onDone(`${pretty} has no 911 address now.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`911 address of ${pretty}`}
      foot={
        removing ? (
          <>
            <Button variant="danger" onClick={() => void remove()} disabled={busy}>
              {busy ? 'Removing…' : 'Remove the address'}
            </Button>
            <Button variant="ghost" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save the address'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {removing ? (
        <Notice tone="warn" title={`Remove ${pretty}’s 911 address?`}>
          If someone calls 911 from {pretty}, emergency services won’t be sent an address, and the call may go to a national call center first. Only do this if no one calls out with this number.
        </Notice>
      ) : (
        <div className="section-stack">
          {number.address && (
            <p className="muted">
              Now: <strong>{oneLine(number.address)}</strong>
            </p>
          )}
          <AddressChooser saved={saved} value={draft} onChange={setDraft} showErrors={tried} />
          <label className="check confirm-check">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
            <span>
              People who call out with {pretty} are at {chosen && chosen.address1 ? <strong>{oneLine(chosen)}</strong> : 'this address'}.
            </span>
          </label>
          {tried && !confirm && <span className="error">Confirm this is where people using the number are.</span>}
          {number.address && (
            <button type="button" className="danger-link link-btn" onClick={() => setRemoving(true)}>
              Remove the 911 address instead
            </button>
          )}
        </div>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/** The name people see when this number calls them (caller ID name), up to 15 characters. */
function CallerName({ d, number, onClose, onDone }: { d: string; number: NumberE911; onClose: () => void; onDone: (message: string) => void }) {
  const pretty = formatPhoneNumber(number.number);
  const [name, setName] = useState(number.callerName?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const bad = !name.trim() ? 'Enter a name.' : !/^[A-Za-z0-9 .,&'-]+$/.test(name) ? 'Letters, numbers, spaces and . , & \' - only.' : '';
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Name on calls from ${pretty}`}
      foot={
        <>
          <Button
            variant="primary"
            disabled={busy || Boolean(bad)}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await api.setCallerName(d, number.number, name.trim());
                onDone(`Calls from ${pretty} will show “${name.trim()}”.`);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field
        label="Name"
        value={name}
        maxLength={15}
        onChange={(e) => setName(e.target.value)}
        error={name && bad ? bad : undefined}
        hint={`${15 - name.length} of 15 letters left. Phone companies update their listings over a few days, and some mobiles show their own contact name instead.`}
        autoFocus
      />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/** Adding, editing or removing one of the company's saved addresses. */
function EditAddress({ d, address, onClose, onDone }: { d: string; address: SavedAddress | null; onClose: () => void; onDone: (message: string) => void }) {
  const [label, setLabel] = useState(address?.label ?? '');
  const [a, setA] = useState<E911Address>(() => (address ? { address1: address.address1, address2: address.address2, city: address.city, state: address.state, zip: address.zip } : { ...EMPTY_ADDRESS }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const errors = tried ? addressErrors(a) : {};
  const labelError = tried && !label.trim() ? 'Name it, like “Main office”.' : undefined;

  async function save() {
    setTried(true);
    if (Object.keys(addressErrors(a)).length || !label.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.saveAddress(d, { ...a, label: label.trim() }, address?.id);
      onDone(address ? `${label.trim()} is saved.` : `${label.trim()} is saved for choosing next time.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={address ? `Saved address: ${address.label}` : 'New saved address'}
      subtitle={address ? 'Numbers already using this address keep it until you change them.' : undefined}
      foot={
        <>
          <Button variant="primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {address && (
            <Button
              variant="ghost"
              className="push-right danger-text"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.removeAddress(d, address.id);
                  onDone(`${address.label} is removed from your saved addresses.`);
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                  setBusy(false);
                }
              }}
            >
              Remove
            </Button>
          )}
        </>
      }
    >
      <div className="section-stack">
        <Field label="Name" value={label} onChange={(e) => setLabel(e.target.value)} error={labelError} placeholder="Main office" maxLength={60} autoFocus />
        <AddressFields value={a} onChange={setA} errors={errors} />
      </div>
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}
